/**
 * Ultra-lightweight event emitter for in-app events
 * Alternative to window events - completely self-contained
 */

type EventHandler<T = any> = (data: T) => void;

class LightEventEmitter {
  private events: { [key: string]: EventHandler[] } = {};

  on<T = any>(event: string, handler: EventHandler<T>): () => void {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(handler);

    // Return cleanup function
    return () => this.off(event, handler);
  }

  emit<T = any>(event: string, data: T): void {
    const handlers = this.events[event];
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  off<T = any>(event: string, handler: EventHandler<T>): void {
    const handlers = this.events[event];
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  clear(event?: string): void {
    if (event) {
      delete this.events[event];
    } else {
      this.events = {};
    }
  }

  // Get current listener count for debugging
  getListenerCount(event: string): number {
    return this.events[event]?.length || 0;
  }

  // Get all registered events for debugging
  getRegisteredEvents(): string[] {
    return Object.keys(this.events);
  }
}

// Create singleton instance
export const eventBus = new LightEventEmitter();

// Balance-specific event helpers
export interface BalanceUpdateDetail {
  currency: string;
  amountChange: number;
  newBalance?: number;
  reason: 'token_creation' | 'token_redemption' | 'topup' | 'transfer' | 'refund' | 'manual';
  tokenKey?: string;
  transactionId?: string;
  metadata?: Record<string, any>;
}

export const emitBalanceUpdate = (detail: BalanceUpdateDetail): void => {
  console.log('💰 Emitting balance update via event bus:', detail);
  eventBus.emit('balance:update', detail);
};

// Signal-only version - emits balance:update with no payload (just signal that topup happened)
export const emitBalanceUpdateSignal = (): void => {
  console.log('💰 Emitting balance:update signal (no payload)');
  eventBus.emit('balance:update', undefined);
};

export const onBalanceUpdate = (callback: (detail?: BalanceUpdateDetail) => void): (() => void) => {
  return eventBus.on('balance:update', callback);
};

// Convenience helpers for specific balance operations
export const emitTokenCreationBalanceUpdate = (
  tokenAmount: number,
  currency: string,
  currentBalance: number,
  tokenKey: string
): void => {
  const balanceChange = -Math.abs(tokenAmount);
  const newBalance = currentBalance + balanceChange;
  
  emitBalanceUpdate({
    currency,
    amountChange: balanceChange,
    newBalance,
    reason: 'token_creation',
    tokenKey
  });
};

export const emitTokenRedemptionBalanceUpdate = (
  tokenAmount: number,
  currency: string,
  currentBalance: number,
  tokenKey: string
): void => {
  const balanceChange = Math.abs(tokenAmount);
  const newBalance = currentBalance + balanceChange;
  
  emitBalanceUpdate({
    currency,
    amountChange: balanceChange,
    newBalance,
    reason: 'token_redemption',
    tokenKey
  });
};

export const emitTopUpBalanceUpdate = (
  amount: number,
  currency: string,
  currentBalance: number,
  transactionId?: string,
  actualBalance?: number // Optional: use fetched balance from API instead of calculating
): void => {
  const balanceChange = Math.abs(amount);
  // Use actual balance from API if provided, otherwise calculate optimistically
  const newBalance = actualBalance !== undefined ? actualBalance : (currentBalance + balanceChange);
  
  emitBalanceUpdate({
    currency,
    amountChange: balanceChange,
    newBalance,
    reason: 'topup',
    transactionId
  });
};

// Balances updated event - signal that balances have been refreshed from platform
// No payload - components should fetch balances themselves if needed
export const emitBalancesUpdated = (): void => {
  console.log('💰 Emitting balances:updated signal (no payload)');
  eventBus.emit('balances:updated', undefined);
};

export const onBalancesUpdated = (callback: () => void): (() => void) => {
  return eventBus.on('balances:updated', callback);
};

// Token created event - emitted when a token is successfully created
export interface TokenCreatedDetail {
  token: {
    key: string;
    amount: number;
    currency: string;
    message?: string;
    type?: string;
    [key: string]: any; // Allow additional token properties
  };
}

export const emitTokenCreated = (detail: TokenCreatedDetail): void => {
  console.log('🎫 Emitting token:created event:', detail);
  eventBus.emit('token:created', detail);
};

export const onTokenCreated = (callback: (detail: TokenCreatedDetail) => void): (() => void) => {
  return eventBus.on('token:created', callback);
};

// eventBus already exported above 